import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SlidersHorizontal, ChevronLeft, ChevronRight, X, ArrowUpDown } from 'lucide-react';
import {
  fetchProducts,
  selectAllDresses,
  selectDressesLoading,
  selectPagination,
} from '../store/slices/dressesSlice';
import {
  selectFilters,
  setPage,
  setSortBy,
  setCategory,
  setMinPrice,
  setMaxPrice,
  setInStockOnly,
} from '../store/slices/filtersSlice';
import FilterSidebar from '../components/FilterSidebar';
import ProductGrid from '../components/ProductGrid';
import { pushViewItemList } from '../utils/gtm';

const SORT_OPTIONS = [
  { value: '-created_at', label: 'Newest First' },
  { value: 'price', label: 'Price: Low to High' },
  { value: '-price', label: 'Price: High to Low' },
  { value: 'name', label: 'Name: A–Z' },
  { value: '-name', label: 'Name: Z–A' },
  { value: '-rating', label: 'Top Rated' },
];

/* ── Pagination ── */
const Pagination = ({ currentPage, totalPages, onPageChange, loading }) => {
  if (totalPages <= 1) return null;

  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || loading}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">Prev</span>
      </button>

      {getPages().map((page, idx) =>
        page === '...' ? (
          <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-400 text-sm">…</span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            disabled={loading}
            className={`min-w-[36px] h-9 px-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-60 ${
              currentPage === page
                ? 'bg-primary-600 text-white border border-primary-600'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || loading}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <span className="hidden sm:inline">Next</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ── Active filter chips ── */
const ActiveFilterChips = ({ filters, dispatch }) => {
  const chips = [];

  if (filters.categoryId) {
    chips.push(
      <span key="cat" className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 text-primary-700 border border-primary-200 rounded-full text-xs font-medium">
        {filters.categoryName}
        <button onClick={() => dispatch(setCategory({ id: null, name: 'All' }))} className="text-primary-500 hover:text-primary-700 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (filters.minPrice !== '') {
    chips.push(
      <span key="min" className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-full text-xs font-medium">
        Min ৳{filters.minPrice}
        <button onClick={() => dispatch(setMinPrice(''))} className="text-gray-400 hover:text-gray-600 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (filters.maxPrice !== '') {
    chips.push(
      <span key="max" className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-full text-xs font-medium">
        Max ৳{filters.maxPrice}
        <button onClick={() => dispatch(setMaxPrice(''))} className="text-gray-400 hover:text-gray-600 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (filters.inStockOnly) {
    chips.push(
      <span key="stock" className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-medium">
        In Stock Only
        <button onClick={() => dispatch(setInStockOnly(false))} className="text-emerald-500 hover:text-emerald-700 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  if (chips.length === 0) return null;
  return <div className="flex flex-wrap gap-2 items-center mb-4">{chips}</div>;
};

/* ── HomePage ── */
const HomePage = () => {
  const dispatch = useDispatch();
  const products = useSelector(selectAllDresses);
  const loading = useSelector(selectDressesLoading);
  const pagination = useSelector(selectPagination);
  const filters = useSelector(selectFilters);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const prevPageRef = useRef(null);
  const topRef = useRef(null);

  const totalPages = filters.pageSize > 0 ? Math.ceil(pagination.count / filters.pageSize) : 0;

  // Fetch on filter change
  useEffect(() => {
    dispatch(fetchProducts({
      categoryId: filters.categoryId,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy: filters.sortBy,
      searchQuery: filters.searchQuery,
      inStockOnly: filters.inStockOnly,
      page: filters.page,
      pageSize: filters.pageSize,
    }));
  }, [
    dispatch,
    filters.categoryId,
    filters.minPrice,
    filters.maxPrice,
    filters.sortBy,
    filters.searchQuery,
    filters.inStockOnly,
    filters.page,
    filters.pageSize,
  ]);

  // Scroll to top on page change (skip first render)
  useEffect(() => {
    if (prevPageRef.current !== null && prevPageRef.current !== filters.page) {
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevPageRef.current = filters.page;
  }, [filters.page]);

  // GA4 view_item_list — fires whenever a new page/filter result set loads
  useEffect(() => {
    if (!loading && products.length > 0) {
      pushViewItemList(products, {
        listId: 0,
        listName: 'All Products',
        page: filters.page,
        pageSize: filters.pageSize,
      });
    }
  }, [products, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTitle = () => {
    if (filters.searchQuery) return `Results for "${filters.searchQuery}"`;
    if (filters.categoryId) return filters.categoryName;
    return 'Our Collection';
  };

  const pageStart = pagination.count > 0 ? (filters.page - 1) * filters.pageSize + 1 : 0;
  const pageEnd = Math.min(filters.page * filters.pageSize, pagination.count);

  return (
    <div ref={topRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex gap-6">

        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-56 xl:w-64 flex-shrink-0">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm sticky top-24 overflow-hidden">
            <FilterSidebar />
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{getTitle()}</h1>
              {!loading && pagination.count > 0 && (
                <p className="text-sm text-gray-500 mt-0.5">
                  Showing {pageStart}–{pageEnd} of {pagination.count.toLocaleString()} products
                </p>
              )}
              {!loading && pagination.count === 0 && (
                <p className="text-sm text-gray-500 mt-0.5">No products found</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Mobile filter button */}
              <button
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
              </button>

              {/* Sort */}
              <div className="flex items-center gap-1.5">
                <ArrowUpDown className="w-4 h-4 text-gray-400 hidden sm:block" />
                <select
                  value={filters.sortBy}
                  onChange={e => dispatch(setSortBy(e.target.value))}
                  className="pl-2 pr-7 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  {SORT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Active chips */}
          <ActiveFilterChips filters={filters} dispatch={dispatch} />

          {/* Grid */}
          <ProductGrid
            products={products}
            loading={loading}
            listId={0}
            listName="All Products"
            startIndex={(filters.page - 1) * filters.pageSize}
          />

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-8">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={p => dispatch(setPage(p))}
                loading={loading}
              />
              <p className="text-center text-xs text-gray-400 mt-3">
                Page {filters.page} of {totalPages}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-full max-w-xs bg-white shadow-2xl overflow-y-auto">
            <FilterSidebar onClose={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
